import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { taskKeys } from './useTasks';

// Types
interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePicture: string | null;
}

interface TaskAssignment {
  id: string;
  taskId: string;
  userId: string;
  units: number;
  work: number;
  workUnit: string;
  user?: User;
  createdAt: string;
  updatedAt: string;
}

interface CreateAssignmentDto {
  userId: string;
  units?: number;
  work?: number;
  workUnit?: string;
}

interface UpdateAssignmentDto {
  units?: number;
  work?: number;
  workUnit?: string;
}

// Query keys
export const assignmentKeys = {
  all: ['assignments'] as const,
  lists: () => [...assignmentKeys.all, 'list'] as const,
  byTask: (taskId: string) => [...assignmentKeys.lists(), 'task', taskId] as const,
  byUser: (userId: string) => [...assignmentKeys.lists(), 'user', userId] as const,
  detail: (assignmentId: string) => [...assignmentKeys.all, 'detail', assignmentId] as const,
};

// Fetch assignments for a task
export function useAssignmentsQuery(taskId: string) {
  return useQuery({
    queryKey: assignmentKeys.byTask(taskId),
    queryFn: async () => {
      const response = await api.get<TaskAssignment[]>(`/tasks/${taskId}/assignments`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 30000,
  });
}

// Fetch all users available for assignment (project members)
export function useProjectMembersQuery(projectId: string) {
  return useQuery({
    queryKey: ['projectMembers', projectId],
    queryFn: async () => {
      const response = await api.get<Array<{ user: User; role: string }>>(
        `/projects/${projectId}/members`
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 60000,
  });
}

// Create assignment mutation
export function useCreateAssignment(taskId: string, sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateAssignmentDto) => {
      const response = await api.post<TaskAssignment>(
        `/tasks/${taskId}/assignments`,
        dto
      );
      return response.data;
    },
    onMutate: async (newAssignment) => {
      await queryClient.cancelQueries({
        queryKey: assignmentKeys.byTask(taskId),
      });

      const previousAssignments = queryClient.getQueryData<TaskAssignment[]>(
        assignmentKeys.byTask(taskId)
      );

      const optimisticAssignment: TaskAssignment = {
        id: `temp-${Date.now()}`,
        taskId,
        userId: newAssignment.userId,
        units: newAssignment.units ?? 100,
        work: newAssignment.work ?? 0,
        workUnit: newAssignment.workUnit ?? 'h',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (previousAssignments) {
        queryClient.setQueryData<TaskAssignment[]>(
          assignmentKeys.byTask(taskId),
          [...previousAssignments, optimisticAssignment]
        );
      }

      return { previousAssignments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAssignments) {
        queryClient.setQueryData(
          assignmentKeys.byTask(taskId),
          context.previousAssignments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byTask(taskId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    },
  });
}

// Update assignment mutation
export function useUpdateAssignment(taskId: string, sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      updates,
    }: {
      assignmentId: string;
      updates: UpdateAssignmentDto;
    }) => {
      const response = await api.patch<TaskAssignment>(
        `/assignments/${assignmentId}`,
        updates
      );
      return response.data;
    },
    onMutate: async ({ assignmentId, updates }) => {
      await queryClient.cancelQueries({
        queryKey: assignmentKeys.byTask(taskId),
      });

      const previousAssignments = queryClient.getQueryData<TaskAssignment[]>(
        assignmentKeys.byTask(taskId)
      );

      if (previousAssignments) {
        queryClient.setQueryData<TaskAssignment[]>(
          assignmentKeys.byTask(taskId),
          previousAssignments.map((a) =>
            a.id === assignmentId
              ? { ...a, ...updates, updatedAt: new Date().toISOString() }
              : a
          )
        );
      }

      return { previousAssignments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAssignments) {
        queryClient.setQueryData(
          assignmentKeys.byTask(taskId),
          context.previousAssignments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byTask(taskId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    },
  });
}

// Delete assignment mutation
export function useDeleteAssignment(taskId: string, sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      await api.delete(`/assignments/${assignmentId}`);
      return assignmentId;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({
        queryKey: assignmentKeys.byTask(taskId),
      });

      const previousAssignments = queryClient.getQueryData<TaskAssignment[]>(
        assignmentKeys.byTask(taskId)
      );

      if (previousAssignments) {
        queryClient.setQueryData<TaskAssignment[]>(
          assignmentKeys.byTask(taskId),
          previousAssignments.filter((a) => a.id !== assignmentId)
        );
      }

      return { previousAssignments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAssignments) {
        queryClient.setQueryData(
          assignmentKeys.byTask(taskId),
          context.previousAssignments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byTask(taskId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    },
  });
}

export type { TaskAssignment, User, CreateAssignmentDto, UpdateAssignmentDto };
